import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Camera, X, MapPin, Tag, CurrencyEur, 
  CircleNotch, Check, ArrowLeft, Storefront, HandHeart,
  UploadSimple, Image as ImageIcon
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CreateListingPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [counties, setCounties] = useState([]);
  const [conditions, setConditions] = useState([]);
  
  // Form fields
  const [listingType, setListingType] = useState("for_sale");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [price, setPrice] = useState("");
  const [negotiable, setNegotiable] = useState(true);
  const [condition, setCondition] = useState("");
  const [location, setLocation] = useState("");
  const [county, setCounty] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email || "");
  const [images, setImages] = useState([]); // Array of {id, url, preview}
  const [uploadingImages, setUploadingImages] = useState(false);
  
  useEffect(() => {
    if (!user) {
      navigate("/marketplace");
      return;
    }
    fetchCategories();
  }, [user, navigate]);
  
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/marketplace/categories`);
      setCategories(res.data.categories);
      setSubcategories(res.data.subcategories);
      setCounties(res.data.counties);
      setConditions(res.data.conditions);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };
  
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Check max images
    if (images.length + files.length > 5) {
      setError("Maximum 5 images allowed per listing");
      return;
    }
    
    setUploadingImages(true);
    setError(null);
    
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const res = await axios.post(`${API}/api/marketplace/upload-images`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Add uploaded images with Cloudinary URLs
      const newImages = res.data.images.map(img => ({
        id: img.id,
        url: img.url,  // Cloudinary CDN URL
        thumbnail: img.thumbnail_url || img.url,
        preview: img.thumbnail_url || img.url  // Use Cloudinary URL for preview
      }));
      
      setImages(prev => [...prev, ...newImages]);
    } catch (err) {
      console.error("Failed to upload images:", err);
      setError(err.response?.data?.detail || "Failed to upload images. Max 10MB per image.");
    } finally {
      setUploadingImages(false);
    }
  };
  
  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const listingData = {
        title,
        description,
        listing_type: listingType,
        category,
        subcategory: subcategory || null,
        price: parseFloat(price),
        currency: "EUR",
        negotiable,
        condition: condition || null,
        location,
        county,
        images: images.map(img => img.id), // Send image IDs
        contact_phone: contactPhone || null,
        contact_email: contactEmail || user.email
      };
      
      await axios.post(`${API}/api/marketplace/listings`, listingData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      setTimeout(() => navigate("/marketplace"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };
  
  if (!user) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <Header />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/marketplace")}
          className="flex items-center gap-2 text-[#6B6661] hover:text-[#0F766E] mb-6"
        >
          <ArrowLeft size={20} />
          Back to Marketplace
        </button>
        
        <div className="bg-white border border-[#E0D4C8] p-6 sm:p-8">
          <h1 className="font-serif text-2xl sm:text-3xl text-[#33302E] mb-2">
            Create New Listing
          </h1>
          <p className="text-[#6B6661] mb-8">
            Fill in the details below to post your item for sale
          </p>
          
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-800 flex items-center gap-2">
              <Check size={20} />
              Listing created successfully! Redirecting...
            </div>
          )}
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Listing Type */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">
                What type of listing? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setListingType("for_sale")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border transition-colors ${
                    listingType === "for_sale"
                      ? "bg-[#0F766E] text-white border-[#0F766E]"
                      : "bg-white border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD]"
                  }`}
                  data-testid="type-for-sale"
                >
                  <Storefront size={20} />
                  I'm Selling
                </button>
                <button
                  type="button"
                  onClick={() => setListingType("wanted")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border transition-colors ${
                    listingType === "wanted"
                      ? "bg-[#D97706] text-white border-[#D97706]"
                      : "bg-white border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD]"
                  }`}
                  data-testid="type-wanted"
                >
                  <HandHeart size={20} />
                  I'm Looking For
                </button>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">
                {listingType === "wanted" ? "What are you looking for?" : "Title"} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                maxLength={100}
                placeholder={listingType === "wanted" 
                  ? "e.g., Looking for John Deere Tractor 80-120HP" 
                  : "e.g., John Deere 6120M Tractor - 2020"}
                className="w-full px-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                data-testid="title-input"
              />
              <p className="mt-1 text-xs text-[#6B6661]">{title.length}/100 characters</p>
            </div>
            
            {/* Category & Subcategory */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={category}
                  onChange={(e) => { setCategory(e.target.value); setSubcategory(""); }}
                  required
                  className="w-full px-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                  data-testid="category-select"
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              
              {category && subcategories[category] && (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">
                    Subcategory
                  </label>
                  <select
                    value={subcategory}
                    onChange={(e) => setSubcategory(e.target.value)}
                    className="w-full px-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                  >
                    <option value="">Select subcategory</option>
                    {subcategories[category].map((sub) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            {/* Price */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  Price (€) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <CurrencyEur size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]" />
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                    data-testid="price-input"
                  />
                </div>
              </div>
              
              <div className="flex items-center">
                <label className="flex items-center gap-3 cursor-pointer mt-6">
                  <input
                    type="checkbox"
                    checked={negotiable}
                    onChange={(e) => setNegotiable(e.target.checked)}
                    className="w-5 h-5 accent-[#0F766E]"
                  />
                  <span className="text-[#374151]">Price is negotiable</span>
                </label>
              </div>
            </div>
            
            {/* Condition */}
            {(category === "machinery" || category === "equipment") && (
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  Condition
                </label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                >
                  <option value="">Select condition</option>
                  {conditions.map((c) => (
                    <option key={c} value={c}>
                      {c.replace("_", " ").split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={5}
                maxLength={2000}
                placeholder="Describe your item in detail. Include year, make, model, hours, any modifications, reason for selling, etc."
                className="w-full px-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E] resize-none"
                data-testid="description-input"
              />
              <p className="mt-1 text-xs text-[#6B6661]">{description.length}/2000 characters</p>
            </div>
            
            {/* Location */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  Location <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]" />
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                    placeholder="e.g., Ballinasloe, Co. Galway"
                    className="w-full pl-10 pr-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  County <span className="text-red-500">*</span>
                </label>
                <select
                  value={county}
                  onChange={(e) => setCounty(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                  data-testid="county-select"
                >
                  <option value="">Select county</option>
                  {counties.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">
                Photos (up to 5)
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {images.map((img, i) => (
                  <div key={img.id || i} className="aspect-square relative group">
                    <img src={img.preview} alt={`Upload ${i+1}`} className="w-full h-full object-cover border border-[#E0D4C8]" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {images.length < 5 && (
                  <label className={`aspect-square border-2 border-dashed border-[#E0D4C8] flex flex-col items-center justify-center cursor-pointer hover:border-[#0F766E] hover:bg-[#F6E9DD] transition-colors ${uploadingImages ? 'opacity-50 pointer-events-none' : ''}`}>
                    {uploadingImages ? (
                      <>
                        <CircleNotch size={24} className="text-[#0F766E] animate-spin" />
                        <span className="text-xs text-[#6B6661] mt-1">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <UploadSimple size={24} className="text-[#8B8580]" />
                        <span className="text-xs text-[#8B8580] mt-1">Add Photo</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImages}
                    />
                  </label>
                )}
              </div>
              <p className="mt-2 text-xs text-[#6B6661]">
                JPEG, PNG, or WebP. Max 5MB per image.
              </p>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="e.g., 087 123 4567"
                  className="w-full px-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                />
              </div>
            </div>
            
            {/* Submit */}
            <div className="flex items-center gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#0F766E] text-white hover:bg-[#0D6B64] disabled:opacity-50 transition-colors"
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <CircleNotch size={20} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check size={20} />
                    Post Listing
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate("/marketplace")}
                className="px-6 py-3 border border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD] transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
